import React, { useState } from 'react';
import { api } from '../../services/api';
import { EA_ROLLER, EaRol } from './constants/roller';
import { TurnstileWidget, turnstileEnabled } from '../Auth/TurnstileWidget';
import { 
  Building2, 
  Shield, 
  User as UserIcon, 
  Mail, 
  Lock, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  ArrowLeft,
  DollarSign,
  Info,
  ChevronRight
} from 'lucide-react';

interface EduAnalizLandingProps {
  onLoginSuccess: (user: any) => void;
  onBack: () => void;
}

export const EduAnalizLanding: React.FC<EduAnalizLandingProps> = ({ onLoginSuccess, onBack }) => {
  // State for Navigation / Pricing
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'semi-annual' | 'annual'>('annual');
  const [activeFormTab, setActiveFormTab] = useState<'login' | 'register'>('login');

  // Login Form State
  const [loginRole, setLoginRole] = useState<EaRol>('teacher');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginInstitutionCode, setLoginInstitutionCode] = useState('');
  const [loginError, setLoginError] = useState('');
  // Giriş, hesap doğrulanmadığı için reddedildiğinde gösterilir
  const [needsVerification, setNeedsVerification] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [loginLoading, setLoginLoading] = useState(false);
  // Cloudflare Turnstile. Anahtar tanımlı değilse `turnstileEnabled` false
  // olur, pencere hiç çizilmez ve giriş eskisi gibi çalışır — yapılandırma
  // eksikken kimse kapıda kalmaz.
  const [turnstileToken, setTurnstileToken] = useState('');
  // Jeton tek kullanımlık: her başarısız denemeden sonra pencere sıfırlanmalı,
  // yoksa ikinci deneme harcanmış jetonla gidip reddediliyor.
  const [turnstileSifirla, setTurnstileSifirla] = useState(0);
  const turnstileTazele = () => { setTurnstileToken(''); setTurnstileSifirla((n) => n + 1); };

  // Registration Form State
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regInstitutionName, setRegInstitutionName] = useState('');
  const [regInstitutionCode, setRegInstitutionCode] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  // Notification State
  const [notif, setNotif] = useState<{ type: 'success' | 'error' | 'warning', msg: string } | null>(null);

  const showNotif = (type: 'success' | 'error' | 'warning', msg: string) => {
    setNotif({ type, msg });
    setTimeout(() => setNotif(null), 4000);
  };

  const [packages, setPackages] = useState<any[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);

  React.useEffect(() => {
    const loadPackages = async () => {
      try {
        const data = await api.getEduAnalizPackages();
        if (data && data.length > 0) {
          setPackages(data);
        }
      } catch (err) {
        console.error("Failed to load packages in landing:", err);
      } finally {
        setPackagesLoading(false);
      }
    };
    loadPackages();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword || !loginInstitutionCode) {
      setLoginError('Lütfen kurum kodu, e-posta ve şifrenizi girin.');
      return;
    }

    if (turnstileEnabled && !turnstileToken) {
      setLoginError('Lütfen robot olmadığınızı doğrulayın.');
      return;
    }

    setLoginLoading(true);
    setLoginError('');
    setNeedsVerification('');
    setResendState('idle');

    try {
      const user = await api.login(loginEmail, loginPassword, true, loginRole, loginInstitutionCode, turnstileToken || undefined);
      if (user) {
        showNotif('success', 'Giriş başarılı! Yönlendiriliyorsunuz...');
        setTimeout(() => onLoginSuccess(user), 1000);
      } else {
        setLoginError('Kurum kodu, e-posta veya şifre hatalı!');
        turnstileTazele();
      }
    } catch (err: any) {
      if (err?.code === 'EMAIL_NOT_VERIFIED') {
        // Şifre doğru, yalnızca adres doğrulanmamış — hata yerine
        // yeniden gönderme seçeneği sunulur.
        setNeedsVerification(err.email || loginEmail);
      } else {
        setLoginError(err.message || 'Giriş yapılamadı.');
      }
      // Denemenin sonucu ne olursa olsun jeton harcandı; yenisi alınmalı.
      turnstileTazele();
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!needsVerification) return;
    setResendState('sending');
    try {
      await api.resendEduAnalizVerification(needsVerification);
    } catch {
      // Uç adres ayrımı yapmadan aynı yanıtı veriyor; kullanıcıya da
      // aynı mesaj gösterilir.
    }
    setResendState('sent');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword || !regInstitutionName || !regInstitutionCode) {
      setRegError('Lütfen tüm alanları doldurunuz.');
      return;
    }

    setRegLoading(true);
    setRegError('');
    setRegSuccess('');

    try {
      // Create a user with admin privileges for the new institution
      const payload = {
        name: regName,
        email: regEmail,
        password: regPassword,
        role: 'admin',
        institutionName: regInstitutionName,
        institutionCode: regInstitutionCode.toUpperCase().replace(/\s/g, ''),
        subscriptionPackage: "EduAnaliz Kurumsal",
        licenseStatus: "active",
        expiryDate: "30 Haziran 2027",
        licenseCode: `EA-${Math.floor(100000 + Math.random() * 900000)}`,
        billingPeriod: "Yıllık"
      };

      const kayit = await api.saveEduAnalizUser(payload);
      if (kayit?.verificationRequired) {
        setRegSuccess(`Kurum hesabınız oluşturuldu. ${regEmail} adresine bir doğrulama bağlantısı gönderdik — giriş yapabilmek için önce bağlantıya tıklayın.`);
        showNotif('success', 'Kayıt başarılı! E-postanızı kontrol edin.');
      } else {
        setRegSuccess('Kurum hesabınız başarıyla oluşturuldu! Şimdi "Giriş Yap" sekmesinden giriş yapabilirsiniz.');
        showNotif('success', 'Kayıt başarılı! Giriş yapabilirsiniz.');
      }
      
      // Auto fill login fields to make it easy
      setLoginEmail(regEmail);
      setLoginInstitutionCode(regInstitutionCode.toUpperCase());
      setLoginRole('admin');
      
      // Switch tab
      setTimeout(() => {
        setActiveFormTab('login');
        // Clear fields
        setRegName('');
        setRegEmail('');
        setRegPassword('');
        setRegInstitutionName('');
        setRegInstitutionCode('');
        setRegSuccess('');
      }, 3000);

    } catch (err: any) {
      setRegError(err.message || 'Kayıt işlemi sırasında bir hata oluştu.');
    } finally {
      setRegLoading(false);
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans overflow-x-hidden relative">
      {/* Subtle Corporate Ambient Lighting */}
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500/5 blur-[120px] pointer-events-none z-0" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-sky-400/5 blur-[120px] pointer-events-none z-0" />

      {/* Toast Notification */}
      {notif && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border backdrop-blur-md transition-all animate-in slide-in-from-top duration-300 ${
          notif.type === 'success' ? 'bg-emerald-50/90 border-emerald-200 text-emerald-700' :
          notif.type === 'warning' ? 'bg-amber-50/90 border-amber-200 text-amber-700' :
          'bg-red-50/90 border-red-200 text-red-700'
        }`}>
          {notif.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className={notif.type === 'warning' ? 'text-amber-500' : 'text-red-500'} />}
          <span className="text-sm font-semibold">{notif.msg}</span>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm py-4 px-6 md:px-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/supervisor-logo-2026.png" alt="Supervisor" className="w-10 h-10 rounded-xl shadow-sm object-cover" />
          <div>
            <h1 className="text-lg font-black tracking-tight text-[#0F2942]">EduAnaliz</h1>
            <p className="text-[9px] text-[#0095FF] font-extrabold uppercase tracking-wider">REHBERLİK & ANALİZ SİSTEMİ</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="hidden md:flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-[#0F2942] transition-colors"
          >
            <ArrowLeft size={16} />
            Supervisor Ana Sayfa
          </button>
          <button 
            onClick={() => scrollToSection('portal-form')}
            className="px-5 py-2.5 bg-[#0F2942] hover:bg-[#0095FF] text-white text-xs font-extrabold rounded-xl transition-all shadow-md shadow-slate-900/10 active:scale-95"
          >
            Giriş & Kayıt
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 md:pt-20 pb-24 px-6 md:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 space-y-6 relative z-10 text-center lg:text-left">
          <span className="px-3.5 py-1.5 bg-blue-50 border border-blue-200/80 rounded-full text-xs font-extrabold text-[#0095FF] uppercase tracking-wide inline-block shadow-sm">
            ✨ YAPAY ZEKA DESTEKLİ ÖLÇME & REHBERLİK
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight text-[#0F2942]">
            Eğitimde <span className="text-[#0095FF]">Veri Odaklı</span> <br className="hidden md:block"/>
            Gelişim Dönemi
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
            Rehberlik servisleri, öğretmenler, yöneticiler ve öğrenciler için özel geliştirilmiş yapay zeka analiz ekosistemi. Akademik takibi dijitalleştirin, başarıyı ölçülebilir kılın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-3">
            <button 
              onClick={() => scrollToSection('portal-form')}
              className="px-8 py-4 bg-[#0F2942] hover:bg-[#0095FF] text-white font-extrabold rounded-xl transition-all shadow-lg shadow-slate-900/15 flex items-center justify-center gap-2 active:scale-95"
            >
              Giriş veya Hesap Oluştur
              <ArrowRight size={18} />
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="px-8 py-4 bg-white border border-slate-200 hover:bg-slate-50 text-[#0F2942] font-extrabold rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 active:scale-95"
            >
              Paketleri ve Fiyatları İncele
            </button>
          </div>
        </div>

        {/* Dashboard Mockup Representation */}
        <div className="lg:col-span-5 relative z-10 animate-in fade-in zoom-in duration-700">
          <div className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-xl shadow-slate-200/60 relative overflow-hidden">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-400 shadow-sm" />
                <span className="w-3 h-3 rounded-full bg-yellow-400 shadow-sm" />
                <span className="w-3 h-3 rounded-full bg-emerald-400 shadow-sm" />
              </div>
              <span className="text-[10px] text-slate-400 font-mono font-medium">eduanaliz.dashboard.json</span>
            </div>
            
            <div className="space-y-4 pt-6">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Okul Başarı Ortalaması</div>
                  <div className="text-xl font-black text-[#0F2942] mt-1">%84.2</div>
                </div>
                <span className="text-xs text-emerald-600 font-extrabold bg-emerald-100/80 px-2 py-0.5 rounded-full">+%4.8</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm">
                  <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Aktif Öğrenci</div>
                  <div className="text-lg font-black text-[#0F2942] mt-1">480</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm">
                  <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Yapılan Ölçek</div>
                  <div className="text-lg font-black text-[#0F2942] mt-1">1,240</div>
                </div>
              </div>

              {/* Progress bar simulation */}
              <div className="space-y-1.5 pt-2">
                <div className="flex justify-between text-xs text-slate-600 font-medium">
                  <span>Müfredat Tamamlanma</span>
                  <span className="text-[#0095FF] font-extrabold">%78</span>
                </div>
                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden border border-slate-200/50">
                  <div className="bg-[#0095FF] h-full rounded-full w-[78%] shadow-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative bg-white border-y border-slate-200/80 py-20 px-6 md:px-12 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto space-y-14">
          <div className="text-center space-y-3">
            <span className="text-xs font-extrabold text-[#0095FF] uppercase tracking-wider bg-blue-50 px-3 py-1 rounded-full border border-blue-200/60">
              Modüler Yapı
            </span>
            <h3 className="text-3xl font-black text-[#0F2942]">Her Rol İçin Özelleştirilmiş Modüller</h3>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm md:text-base font-medium">
              EduAnaliz, okuldaki tüm paydaşların (yönetici, öğretmen, rehberlik servisi ve öğrenci) ihtiyaç duyduğu araçları tek bir panelde birleştirir.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Rehberlik Servisi', desc: 'Öğrenci kişilik ve gelişim envanterleri, hazır ölçekler ve detaylı rehberlik takip tabloları.', icon: Shield, color: 'blue' },
              { title: 'Öğretmenler', desc: 'Sınıf başarı analizleri, konu bazlı eksik belirleme araçları ve interaktif ödev modülü.', icon: UserIcon, color: 'sky' },
              { title: 'Yöneticiler', desc: 'Kurum genelindeki tüm sınıfların akademik durumları, lisans durumları ve gelişmiş filtreli istatistik raporları.', icon: Building2, color: 'navy' },
              { title: 'Öğrenciler', desc: 'Bireysel hedefler, sınav karnesi grafikleri, yol haritaları ve konu eksikleri analizleri.', icon: CheckCircle2, color: 'emerald' }
            ].map((feat, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 p-6 rounded-3xl space-y-4 hover:bg-white hover:shadow-xl hover:border-blue-200 transition-all shadow-sm group">
                <div className={`p-3 rounded-2xl ${
                  feat.color === 'blue' ? 'bg-blue-100/80 text-blue-600' :
                  feat.color === 'sky' ? 'bg-sky-100/80 text-sky-600' :
                  feat.color === 'navy' ? 'bg-slate-100 text-[#0F2942]' :
                  'bg-emerald-100/80 text-emerald-600'
                } w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm`}>
                  <feat.icon size={22} />
                </div>
                <h4 className="font-extrabold text-[#0F2942] text-lg">{feat.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed font-medium">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 md:px-12 max-w-7xl mx-auto space-y-16 relative z-10">
        <div className="text-center space-y-5">
          <span className="text-xs font-extrabold text-[#0095FF] uppercase tracking-wider bg-blue-50 px-3.5 py-1.5 rounded-full border border-blue-200/60">
            Esnek Paketler
          </span>
          <h3 className="text-3xl md:text-4xl font-black text-[#0F2942]">Kurumunuza Uygun Fiyatlandırma Seçenekleri</h3>
          <p className="text-slate-600 max-w-2xl mx-auto text-sm md:text-base font-medium">
            Okulunuzun boyutuna göre seçiminizi yapın. Havale ödemelerinde %5 ekstra indirim uygulanmaktadır.
          </p>
          
          {/* Billing Period Toggle */}
          <div className="inline-flex items-center gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl shadow-sm">
            <button 
              onClick={() => setBillingPeriod('monthly')}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${billingPeriod === 'monthly' ? 'bg-[#0F2942] text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Aylık
            </button>
            <button 
              onClick={() => setBillingPeriod('semi-annual')}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${billingPeriod === 'semi-annual' ? 'bg-[#0F2942] text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
            >
              6 Aylık
            </button>
            <button 
              onClick={() => setBillingPeriod('annual')}
              className={`px-5 py-2 rounded-xl text-xs font-bold transition-all ${billingPeriod === 'annual' ? 'bg-[#0F2942] text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Yıllık
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {packagesLoading ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-3xl p-8 flex flex-col gap-6 animate-pulse shadow-md">
                <div className="space-y-2">
                  <div className="h-5 bg-slate-200 rounded-lg w-2/3" />
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-4/5" />
                </div>
                <div className="h-10 bg-slate-200 rounded-lg w-1/2" />
                <div className="h-[1px] bg-slate-100" />
                <div className="space-y-2">
                  {[1,2,3,4].map(j => <div key={j} className="h-3 bg-slate-100 rounded w-full" />)}
                </div>
                <div className="mt-auto h-12 bg-slate-200 rounded-xl" />
              </div>
            ))
          ) : packages.map((plan) => (
            <div 
              key={plan.id} 
              className={`bg-white border rounded-3xl p-8 flex flex-col justify-between relative overflow-hidden transition-all hover:-translate-y-1.5 duration-300 ${
                plan.isPopular ? 'border-2 border-[#0095FF] shadow-xl shadow-blue-500/10' : 'border-slate-200 shadow-md'
              }`}
            >
              {plan.isPopular && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-[#0095FF] text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-sm">En Avantajlı</div>
              )}
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-xl font-bold text-[#0F2942]">{plan.name}</h4>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed font-medium">{plan.description}</p>
                </div>
                
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-[#0F2942]">
                    {plan.prices?.[billingPeriod] || '-'}
                  </span>
                  <span className="text-xs text-slate-500 font-medium">/ {billingPeriod === 'monthly' ? 'aylık' : billingPeriod === 'semi-annual' ? '6 aylık' : 'yıllık'}</span>
                </div>

                <div className="h-[1px] bg-slate-100" />

                <ul className="space-y-3">
                  {(plan.features || []).map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-xs text-slate-700 leading-relaxed font-medium">
                      <CheckCircle2 size={15} className="text-[#0095FF] mt-0.5 flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-8 mt-auto">
                <button
                  type="button"
                  onClick={() => {
                    setActiveFormTab('register');
                    setRegInstitutionName(plan.name + ' Deneme');
                    scrollToSection('portal-form');
                  }}
                  className={`w-full py-3.5 font-bold rounded-xl transition-all text-xs active:scale-95 ${
                    plan.isPopular 
                      ? 'bg-[#0095FF] hover:bg-[#0F2942] text-white shadow-lg shadow-blue-500/20' 
                      : 'bg-slate-50 hover:bg-slate-100 text-[#0F2942] border border-slate-200 shadow-sm'
                  }`}
                >
                  Hesap Oluştur ve Başla
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Interactive Registration / Login Form Section */}
      <section id="portal-form" className="py-20 px-6 md:px-12 relative z-10 bg-slate-100/60 border-t border-slate-200">
        <div className="max-w-xl mx-auto space-y-8 animate-in fade-in duration-500">
          <div className="text-center space-y-2">
            <h3 className="text-2xl md:text-3xl font-black text-[#0F2942]">EduAnaliz Giriş & Kayıt Portalı</h3>
            <p className="text-slate-600 text-xs md:text-sm font-medium">
              Mevcut bir hesabınız varsa giriş yapın veya yeni bir kurum oluşturarak hemen sistemi deneyin.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/70">
            {/* Tab Header */}
            <div className="flex border-b border-slate-200 bg-slate-50">
              <button
                onClick={() => { setActiveFormTab('login'); setLoginError(''); setNeedsVerification(''); setResendState('idle'); }}
                className={`flex-1 py-4 text-xs font-black tracking-wide uppercase transition-all border-b-2 ${
                  activeFormTab === 'login' ? 'border-[#0095FF] text-[#0095FF] bg-white' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                🔑 Sisteme Giriş Yap
              </button>
              <button
                onClick={() => { setActiveFormTab('register'); setRegError(''); }}
                className={`flex-1 py-4 text-xs font-black tracking-wide uppercase transition-all border-b-2 ${
                  activeFormTab === 'register' ? 'border-[#0095FF] text-[#0095FF] bg-white' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                🏫 Kurum Hesabı Oluştur
              </button>
            </div>

            <div className="p-6 md:p-8">
              {/* LOGIN FORM */}
              {activeFormTab === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-6">
                  {loginError && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl flex items-center gap-2 shadow-sm">
                      <AlertCircle size={16} className="flex-shrink-0" />
                      <span className="font-medium">{loginError}</span>
                    </div>
                  )}

                  {needsVerification && (
                    <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl shadow-sm space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold mb-1">E-posta adresinizi doğrulayın</p>
                          <p className="leading-relaxed">
                            <span className="font-semibold">{needsVerification}</span> adresine gönderdiğimiz
                            bağlantıya tıklayarak hesabınızı etkinleştirin.
                          </p>
                        </div>
                      </div>
                      {resendState === 'sent' ? (
                        <p className="font-semibold text-emerald-700">
                          Yeni bir doğrulama bağlantısı gönderildi. Gelen kutunuzu kontrol edin.
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resendState === 'sending'}
                          className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-bold rounded-lg transition-colors">
                          {resendState === 'sending' ? 'Gönderiliyor...' : 'Bağlantıyı Yeniden Gönder'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Rol seçimi AÇILIR LİSTE: beş rol 2x4 ızgarada tek
                      düğmeyi alta düşürüyordu ve her yeni rol düzeni
                      yeniden bozacaktı. Liste sayıdan bağımsız duruyor. */}
                  <div className="space-y-2">
                    <label htmlFor="ea-giris-rol" className="text-xs font-bold text-slate-600 uppercase tracking-wide">Rol Seçiniz</label>
                    <select
                      id="ea-giris-rol"
                      value={loginRole}
                      onChange={(e) => setLoginRole(e.target.value as EaRol)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] focus:bg-white transition-all text-sm shadow-sm font-semibold"
                    >
                      {EA_ROLLER.map((r) => (
                        <option key={r.id} value={r.id}>{r.icon}  {r.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Kurum Kodu</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        required
                        placeholder="Kurum Kodunuz (Örn: ANADOLULISESI)"
                        value={loginInstitutionCode}
                        onChange={(e) => setLoginInstitutionCode(e.target.value.toUpperCase())}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">E-posta Adresi</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="email"
                        required
                        placeholder="e-posta@adresiniz.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Şifre</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                      />
                    </div>
                  </div>

                  <TurnstileWidget
                    resetKey={turnstileSifirla}
                    onVerify={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken('')}
                  />

                  <button
                    type="submit"
                    disabled={loginLoading || (turnstileEnabled && !turnstileToken)}
                    className="w-full py-3.5 bg-[#0F2942] hover:bg-[#0095FF] text-white font-extrabold rounded-xl transition-all shadow-lg shadow-slate-900/15 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loginLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Sisteme Güvenli Giriş Yap
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                /* REGISTRATION FORM */
                <form onSubmit={handleRegister} className="space-y-5">
                  {regError && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl flex items-center gap-2 shadow-sm">
                      <AlertCircle size={16} className="flex-shrink-0" />
                      <span className="font-medium">{regError}</span>
                    </div>
                  )}

                  {regSuccess && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-xl flex items-center gap-2 shadow-sm">
                      <CheckCircle2 size={16} className="flex-shrink-0 text-emerald-500" />
                      <span className="font-medium">{regSuccess}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Yönetici Adı Soyadı</label>
                    <input
                      type="text"
                      required
                      placeholder="Ad Soyad"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Yönetici E-posta Adresi</label>
                    <input
                      type="email"
                      required
                      placeholder="admin@okuladi.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Şifre</label>
                    <input
                      type="password"
                      required
                      placeholder="En az 6 karakterli şifre"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Kurum/Okul Adı</label>
                    <input
                      type="text"
                      required
                      placeholder="Örn: Supervisor Fen Lisesi"
                      value={regInstitutionName}
                      onChange={(e) => setRegInstitutionName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">İstediğiniz Kurum Giriş Kodu</label>
                    <input
                      type="text"
                      required
                      placeholder="Örn: SUPERVISORFEN"
                      value={regInstitutionCode}
                      onChange={(e) => setRegInstitutionCode(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-[#0095FF] transition-all text-sm"
                    />
                    <p className="text-[10px] text-slate-500 font-medium ml-1">Okulunuzun diğer öğretmen ve öğrencileri sisteme giriş yaparken bu kodu kullanacaktır.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={regLoading}
                    className="w-full py-3.5 bg-[#0F2942] hover:bg-[#0095FF] text-white font-extrabold rounded-xl transition-all shadow-lg shadow-slate-900/15 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {regLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Kurumu ve Yönetici Hesabını Kaydet
                        <ChevronRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-white border-t border-slate-200 py-10 text-center text-xs text-slate-500 space-y-4 z-10">
        <div className="flex justify-center gap-6 text-slate-600 font-semibold">
          <button onClick={onBack} className="hover:text-[#0095FF] transition-colors">Ana Sayfa</button>
          <span>•</span>
          <button onClick={() => scrollToSection('pricing')} className="hover:text-[#0095FF] transition-colors">Fiyatlandırma</button>
          <span>•</span>
          <button onClick={() => scrollToSection('portal-form')} className="hover:text-[#0095FF] transition-colors">Kullanıcı Girişi</button>
        </div>
        <p className="font-medium">© 2026 Supervisor Eğitim Hizmetleri A.Ş. Tüm Hakları Saklıdır.</p>
      </footer>
    </div>
  );
};
